// AGC: Geometry utility functions for polygon calculations
// AGC: Used for area and perimeter calculations of field boundaries

/**
 * AGC: Calculate polygon area in hectares using Shoelace formula
 * AGC: With geodesic correction for accurate Earth surface calculations
 * @param coords - Array of [lat, lng] coordinates
 * @returns Area in hectares
 */
export const calculatePolygonArea = (coords: [number, number][]): number => {
  if (coords.length < 3) return 0
  
  // AGC: Using Shoelace formula with Earth radius correction
  let area = 0
  const n = coords.length
  
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n
    area += coords[i][1] * coords[j][0]
    area -= coords[j][1] * coords[i][0]
  }
  
  area = Math.abs(area) / 2
  
  // AGC: Convert to hectares using geodesic approximation
  // AGC: 1 degree latitude ≈ 111,320 meters
  // AGC: 1 degree longitude ≈ 111,320 * cos(latitude) meters
  const latMid = coords.reduce((sum, c) => sum + c[0], 0) / n
  const metersPerDegreeLat = 111320
  const metersPerDegreeLng = 111320 * Math.cos(latMid * Math.PI / 180)
  
  const areaInSquareMeters = area * metersPerDegreeLat * metersPerDegreeLng
  return areaInSquareMeters / 10000 // AGC: Convert to hectares
}

/**
 * AGC: Calculate polygon perimeter in meters
 * AGC: Uses Haversine formula for accurate distance calculations
 * @param coords - Array of [lat, lng] coordinates
 * @returns Perimeter in meters
 */
export const calculatePerimeter = (coords: [number, number][]): number => {
  if (coords.length < 2) return 0
  
  let perimeter = 0
  const n = coords.length
  
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n
    perimeter += haversineDistance(coords[i], coords[j])
  }
  
  return perimeter
}

/**
 * AGC: Calculate distance between two points using Haversine formula
 * @param point1 - [lat, lng]
 * @param point2 - [lat, lng]
 * @returns Distance in meters
 */
export const haversineDistance = (
  point1: [number, number], 
  point2: [number, number]
): number => {
  const R = 6371000 // AGC: Earth's radius in meters
  const lat1 = point1[0] * Math.PI / 180
  const lat2 = point2[0] * Math.PI / 180
  const deltaLat = (point2[0] - point1[0]) * Math.PI / 180
  const deltaLng = (point2[1] - point1[1]) * Math.PI / 180

  const a = Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
            Math.cos(lat1) * Math.cos(lat2) *
            Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

  return R * c
}

/**
 * AGC: Find the two closest points in a path to close a polygon
 * AGC: Used when GPS path doesn't perfectly close
 * @param path - Array of coordinates
 * @returns Index of the closest point to the starting point
 */
export const findClosingPointIndex = (path: [number, number][]): number => {
  if (path.length < 3) return -1
  
  const startPoint = path[0]
  let minDistance = Infinity
  let closestIndex = -1
  
  // AGC: Search from 70% of the path onwards to avoid false positives
  const searchStart = Math.floor(path.length * 0.7)
  
  for (let i = searchStart; i < path.length; i++) {
    const distance = haversineDistance(startPoint, path[i])
    if (distance < minDistance) {
      minDistance = distance
      closestIndex = i
    }
  }
  
  return closestIndex
}

/**
 * AGC: Format area in hectares for display
 * @param hectares - Area value
 * @returns Formatted string
 */
export const formatAreaHa = (hectares: number): string => {
  if (hectares < 0.01) {
    return `${(hectares * 10000).toFixed(0)} m²`
  }
  if (hectares < 1) {
    return `${hectares.toFixed(2)} ha`
  }
  return `${hectares.toFixed(1)} ha`
}

/**
 * AGC: Format perimeter in meters for display
 * @param meters - Perimeter value
 * @returns Formatted string
 */
export const formatPerimeterM = (meters: number): string => {
  if (meters < 1000) {
    return `${Math.round(meters)} m`
  }
  return `${(meters / 1000).toFixed(2)} km`
}

/**
 * AGC: Validate polygon coordinates
 * @param coords - Array of coordinates
 * @returns True if valid polygon (≥3 points)
 */
export const isValidPolygon = (coords: [number, number][]): boolean => {
  return coords.length >= 3
}

/**
 * AGC: Calculate polygon centroid (center point)
 * @param coords - Array of coordinates
 * @returns Center point [lat, lng]
 */
export const calculateCentroid = (coords: [number, number][]): [number, number] => {
  if (coords.length === 0) return [0, 0]
  
  const sumLat = coords.reduce((sum, c) => sum + c[0], 0)
  const sumLng = coords.reduce((sum, c) => sum + c[1], 0)
  
  return [sumLat / coords.length, sumLng / coords.length]
}






